import logLevel from 'loglevel';

import messages from '../../constants/logMessages.json';
import { shouldShowQalLogger } from '../../constants/qalEnvironments';

// Use a named logger so QAL's log level is isolated from the host
// application's default loglevel instance.
const logger = logLevel.getLogger('qal');

const { errorHeading, errorBold, errorRegular } = messages;

export type MessageKeys = keyof typeof messages;
interface LogOptions {
  logLevelParam: 'error' | 'warn' | 'info' | 'debug' | 'trace' | 'reset';
  explicitlyConfigured: boolean;
  omitDataObject: boolean;
  omitEvents: string[];
}

// Initialise logOptions
function createDefaultLogOptions(): LogOptions {
  return {
    logLevelParam: 'reset',
    explicitlyConfigured: false,
    omitDataObject: false,
    omitEvents: [],
  };
}
const logOptions = createDefaultLogOptions();

export const getMessage = (
  messageKey: MessageKeys,
  values: Array<string> = []
): string => {
  const messageTemplate: string = `%cQAL: ${messages[messageKey]}`;
  return values.reduce((msg, value, index) => {
    const regex = new RegExp(`\\{${index}\\}`, 'g');
    return msg.replace(regex, value.toString());
  }, messageTemplate);
};

function getLocalStorage(): Storage | undefined {
  try {
    if (typeof globalThis === 'undefined') return undefined;
    const maybeStorage = (globalThis as { localStorage?: Storage })
      .localStorage;
    return typeof maybeStorage === 'undefined' ? undefined : maybeStorage;
  } catch {
    return undefined;
  }
}

function setLogOptions(): void {
  const storage = getLocalStorage();
  if (!storage) return;

  const logType = storage.getItem('analytics-logger-enabled');
  if (!logType) return;
  logOptions.explicitlyConfigured = true;

  const getLogLevelParam = (bool: boolean) => (bool ? 'trace' : 'reset');

  try {
    const parsedLog = JSON.parse(logType);
    if (typeof parsedLog === 'object' && parsedLog !== null) {
      const { omitDataObject, omitEvents, enabled } = parsedLog;
      if (omitDataObject) logOptions.omitDataObject = omitDataObject;
      if (Array.isArray(omitEvents) && omitEvents.length > 0) {
        logOptions.omitEvents = omitEvents;
      }
      logOptions.logLevelParam = getLogLevelParam(enabled);
      return;
    }
    logOptions.logLevelParam = getLogLevelParam(parsedLog);
  } catch (e) {
    logger.info(
      getMessage('logObjectError', [e as string]),
      errorHeading,
      errorBold,
      errorRegular
    );
  }
}

const validateLogLevel = (logLevelParam: string) => {
  const validLevels = ['error', 'warn', 'info', 'debug', 'trace', 'reset'];
  return validLevels.includes(logLevelParam);
};

// Run on module load.
setLogOptions();

function setLogLevel(environment: string) {
  const { explicitlyConfigured, logLevelParam } = logOptions;

  // Immediately disable logs if local storage is inaccessible or parameters are invalid
  if (
    !getLocalStorage() ||
    !environment ||
    !logLevelParam ||
    !validateLogLevel(logLevelParam)
  ) {
    logger.disableAll();
    return;
  }

  if (!shouldShowQalLogger(environment)) {
    logger.disableAll();
    return;
  }

  // Lower environments show diagnostics by default. A localStorage setting can
  // still narrow the level or explicitly disable them for an individual browser.
  if (!explicitlyConfigured || /info|trace/.test(logLevelParam)) {
    logger.enableAll();
    return;
  }

  // User explicitly opted out via localStorage (e.g. analytics-logger-enabled: false).
  if (logLevelParam === 'reset') {
    logger.disableAll();
    return;
  }
  if (/error|warn|debug/.test(logLevelParam)) {
    logger.setLevel(logLevelParam);
    return;
  }
  logger.disableAll();
}

export default {
  setLogLevel,
  getLogOptions: () => logOptions,
  getLevel: logger.getLevel,
  trace: logger.trace,
  debug: logger.debug,
  info: logger.info,
  warn: logger.warn,
  error: logger.error,
};
